import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "list", "template", "addBtn",
    "card", "cardNumber", "cardTitle", "cardSubtitle", "cardContent",
    "statusBadge", "chevron", "manualReveal", "positionInput", "removeBtn", "saveBtn",
    "leaseholdReveal", "leaseholdDetailsReveal",
  ]

  MIN_CARDS = 2
  MAX_CARDS = 5
  BADGE_STATES = {
    "not-started": { label: "Not started", classes: ["bg-black-50", "text-black-400"] },
    "in-progress": { label: "In progress", classes: ["bg-blue-100", "text-blue-1000"] },
    complete: { label: "Complete", classes: ["bg-green-100", "text-green-1000"] },
  }
  PRIMARY_SUBTITLE = "Used to identify the valuation region."
  SECONDARY_SUBTITLE = "Address not yet entered"

  connect() {
    // Server-rendered cards occupy indices 0..n-1; the counter only ever moves forward, so
    // clone indices stay unique even when cards are removed (Rails needs unique, not
    // contiguous, nested-attribute indices).
    this.nextIndex = this.cardTargets.length
    this._onFieldActivity = this._onFieldActivity.bind(this)
    this.element.addEventListener("input", this._onFieldActivity)
    this.element.addEventListener("change", this._onFieldActivity)
    this.#syncCount(this.#currentCount())
    this.#renumber()
    this.#updateUI()
  }

  disconnect() {
    this.element.removeEventListener("input", this._onFieldActivity)
    this.element.removeEventListener("change", this._onFieldActivity)
  }

  countChanged(event) {
    const raw = event.detail?.count ?? this.#currentCount()
    this.#syncCount(parseInt(raw, 10))
  }

  reset() {
    this.#listCards().forEach(card => card.remove())
    const primary = this.cardTargets.find(card => card.dataset.nqrCardPrimary === "true")
    if (primary) {
      delete primary.dataset.nqrCardSaved
      this.#setSubtitle(primary, this.PRIMARY_SUBTITLE)
      this.#clearCardErrors(primary)
      this.#setManualReveal(primary, false)
      this.#setExpanded(primary, true)
    }
    this.#updateUI()
  }

  add() {
    if (this.cardTargets.length >= this.MAX_CARDS) return
    const card = this.#appendCardFromTemplate()
    this.#expandOnly(card)
    this.#renumber()
    this.#syncBackCount()
    this.#updateUI()
    this.#notifyForm()
  }

  remove(event) {
    const card = this.#cardFor(event)
    if (!card || card.dataset.nqrCardPrimary === "true") return
    card.remove()
    this.#renumber()
    this.#syncBackCount()
    this.#updateUI()
    this.#notifyForm()
  }

  toggle(event) {
    const card = this.#cardFor(event)
    if (!card) return
    if (this.#expanded(card)) {
      this.#setExpanded(card, false)
    } else {
      this.#expandOnly(card)
    }
  }

  toggleManualAddress(event) {
    const card = this.#cardFor(event)
    if (!card) return
    this.#setManualReveal(card, !this.#manualRevealOpen(card))
  }

  syncTenureChildren(event) {
    const card = this.#cardFor(event)
    if (!card) return
    this.#syncTenureChildren(card)
  }

  save(event) {
    const card = this.#cardFor(event)
    if (!card) return
    const missing = this.#missingRequiredFields(card)
    if (missing.length > 0) {
      this.#refreshCardErrors(card)
      return
    }
    this.#clearCardErrors(card)
    card.dataset.nqrCardSaved = "true"
    this.#setSubtitle(card, this.#addressSummary(card) || this.#defaultSubtitle(card))
    this.#updateCard(card)
    this.#setExpanded(card, false)
  }

  // Any edit in a card is data activity: a saved card drops back to In progress until it is
  // saved again, and picking a lookup result reveals the populated manual address fields.
  _onFieldActivity(event) {
    const card = event.target.closest?.('[data-nqr-portfolio-properties-target="card"]')
    if (!card) return
    const propertyField = (event.target.name || "").includes("properties_attributes")
    const lookupSelect = !!event.target.closest('[data-nqr-address-lookup-target="list"]')
    if (propertyField || lookupSelect) delete card.dataset.nqrCardSaved
    if (lookupSelect) this.#setManualReveal(card, true)
    // Inline errors only exist after a failed save attempt; while present they track the
    // fields live, but they are never introduced by ordinary typing.
    if (card.querySelector("[data-nqr-card-error]")) this.#refreshCardErrors(card)
    this.#updateCard(card)
  }

  #syncCount(count) {
    if (!(count >= this.MIN_CARDS && count <= this.MAX_CARDS)) return
    const target = count - 1
    while (this.#listCards().length < target) {
      const card = this.#appendCardFromTemplate()
      this.#setExpanded(card, false)
    }
    // Shrink from the end only — kept cards preserve everything the user entered.
    while (this.#listCards().length > target) {
      this.#listCards().at(-1).remove()
    }
    this.#renumber()
    this.#updateUI()
  }

  #appendCardFromTemplate() {
    const html = this.templateTarget.innerHTML.replaceAll("__INDEX__", String(this.nextIndex++))
    this.listTarget.insertAdjacentHTML("beforeend", html)
    return this.listTarget.lastElementChild
  }

  // Adding or removing a card makes the card list the source of truth for the portfolio
  // count, so the Step 2 hidden input and radio are written back to match — otherwise the
  // submission could carry a portfolio_property_count that contradicts the property rows.
  #syncBackCount() {
    const total = this.cardTargets.length
    const form = this.element.closest("form")
    if (!form) return
    const countInput = form.querySelector('input[name="quote_request[portfolio_property_count]"]')
    if (countInput) countInput.value = String(total)
    const radio = form.querySelector(`input[name="portfolio_property_count_option"][value="${total}"]`)
    if (radio) radio.checked = true
  }

  #notifyForm() {
    this.element.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #renumber() {
    this.cardTargets.forEach((card, i) => {
      const number = String(i + 1)
      const numberBadge = this.cardNumberTargets.find(el => card.contains(el))
      if (numberBadge) numberBadge.textContent = number
      const title = this.cardTitleTargets.find(el => card.contains(el))
      if (title) title.textContent = `Property ${number}`
      const position = this.positionInputTargets.find(el => card.contains(el))
      if (position) position.value = number
      card.querySelectorAll("[data-nqr-required-label]").forEach(el => {
        el.dataset.nqrRequiredLabel = el.dataset.nqrRequiredLabel
          .replace(/^Property [^–]*– /, `Property ${number} – `)
      })
    })
  }

  #updateUI() {
    const total = this.cardTargets.length
    if (this.hasAddBtnTarget) this.addBtnTarget.classList.toggle("hidden", total >= this.MAX_CARDS)
    this.removeBtnTargets.forEach(btn => btn.classList.toggle("hidden", total <= this.MIN_CARDS))
    this.cardTargets.forEach(card => this.#updateCard(card))
  }

  #updateCard(card) {
    this.#updateBadge(card)
    const saveBtn = this.saveBtnTargets.find(el => card.contains(el))
    if (saveBtn) saveBtn.disabled = this.#missingRequiredFields(card).length > 0
  }

  #updateBadge(card) {
    const badge = this.statusBadgeTargets.find(el => card.contains(el))
    if (!badge) return
    let state = "not-started"
    if (card.dataset.nqrCardSaved === "true") state = "complete"
    else if (this.#cardFields(card).some(field => this.#fieldValue(field) !== "")) state = "in-progress"
    Object.values(this.BADGE_STATES).forEach(({ classes }) => badge.classList.remove(...classes))
    badge.classList.add(...this.BADGE_STATES[state].classes)
    badge.textContent = this.BADGE_STATES[state].label
  }

  #missingRequiredFields(card) {
    return Array.from(card.querySelectorAll('[data-nqr-required="true"]'))
      .filter(el => this.#requiredValue(el) === "")
  }

  #refreshCardErrors(card) {
    const missing = this.#missingRequiredFields(card)
    card.querySelectorAll('[data-nqr-required="true"]').forEach(el => {
      this.#toggleFieldError(el, missing.includes(el))
    })
  }

  #clearCardErrors(card) {
    card.querySelectorAll("[data-nqr-card-error]").forEach(el => el.remove())
    card.querySelectorAll(".border-red-300").forEach(el => el.classList.remove("border-red-300"))
  }

  #toggleFieldError(el, show) {
    const host = el.matches("input, textarea, select")
      ? (el.closest('[data-controller="input"]') || el.parentElement)
      : el
    const input = host.querySelector('input:not([type="hidden"]), textarea')
    let error = host.querySelector("[data-nqr-card-error]")
    input?.classList?.toggle("border-red-300", show)
    if (!show) {
      error?.remove()
      return
    }
    if (!error) {
      error = document.createElement("p")
      error.dataset.nqrCardError = ""
      error.className = "mt-8 text-paragraph-s text-red-1000"
      host.appendChild(error)
    }
    error.textContent = "This field is required"
  }

  // A card's fields are its nested-attribute inputs only — the address search box and the
  // hidden position input never count towards the status badge.
  #cardFields(card) {
    return Array.from(
      card.querySelectorAll(
        'input[name*="properties_attributes"], textarea[name*="properties_attributes"], select[name*="properties_attributes"]'
      )
    ).filter(el => !el.name.endsWith("[position]"))
  }

  #requiredValue(el) {
    if (el.matches("input, textarea, select")) return this.#fieldValue(el)
    const checked = el.querySelector('input[type="radio"]:checked')
    if (checked) return checked.value
    if (el.querySelector('input[type="radio"]')) return ""
    const input = el.querySelector('input:not([type="radio"]):not([type="checkbox"]), textarea, select')
    return input ? this.#fieldValue(input) : ""
  }

  #fieldValue(el) {
    if (el.type === "radio" || el.type === "checkbox") return el.checked ? el.value : ""
    return (el.value || "").trim()
  }

  #addressSummary(card) {
    const part = target => card.querySelector(`[data-nqr-address-lookup-target="${target}"]`)?.value?.trim()
    return [part("line1"), part("postcode")].filter(Boolean).join(", ")
  }

  #defaultSubtitle(card) {
    return card.dataset.nqrCardPrimary === "true" ? this.PRIMARY_SUBTITLE : this.SECONDARY_SUBTITLE
  }

  #setSubtitle(card, text) {
    const subtitle = this.cardSubtitleTargets.find(el => card.contains(el))
    if (subtitle) subtitle.textContent = text
  }

  #cardFor(event) {
    return event.currentTarget.closest('[data-nqr-portfolio-properties-target="card"]')
  }

  #listCards() {
    return this.cardTargets.filter(card => this.listTarget.contains(card))
  }

  #expanded(card) {
    const content = this.cardContentTargets.find(el => card.contains(el))
    return !!content && !content.classList.contains("hidden")
  }

  #expandOnly(card) {
    this.cardTargets.forEach(other => {
      if (other !== card) this.#setExpanded(other, false)
    })
    this.#setExpanded(card, true)
  }

  #setExpanded(card, expanded) {
    const content = this.cardContentTargets.find(el => card.contains(el))
    if (content) content.classList.toggle("hidden", !expanded)
    const chevron = this.chevronTargets.find(el => card.contains(el))
    if (chevron) chevron.classList.toggle("rotate-180", expanded)
    card.classList.toggle("border-brand-1000", expanded)
  }

  #manualRevealOpen(card) {
    const reveal = this.manualRevealTargets.find(el => card.contains(el))
    return !!reveal && !reveal.classList.contains("max-h-0")
  }

  #setManualReveal(card, open) {
    this.#setReveal(this.manualRevealTargets.find(el => card.contains(el)), open)
  }

  #setReveal(reveal, open) {
    if (!reveal) return
    if (open) {
      reveal.classList.remove("max-h-0", "opacity-0", "overflow-hidden")
      reveal.classList.add("max-h-[1200px]", "opacity-100")
    } else {
      reveal.classList.add("max-h-0", "opacity-0", "overflow-hidden")
      reveal.classList.remove("max-h-[1200px]", "opacity-100")
    }
  }

  // Hidden answers are cleared as they hide so a stale selection can never be submitted;
  // the server clears them again as a backstop.
  #syncTenureChildren(card) {
    const leaseholdOpen = this.#checkedValue(card, "[tenure_code]") === "long_leasehold"
    this.#setReveal(this.leaseholdRevealTargets.find(el => card.contains(el)), leaseholdOpen)
    if (!leaseholdOpen) {
      card.querySelectorAll('input[name$="[is_remaining_term_less_than_85_years]"]')
        .forEach(radio => { radio.checked = false })
    }
    const detailsOpen = leaseholdOpen
      && this.#checkedValue(card, "[is_remaining_term_less_than_85_years]") === "true"
    this.#setReveal(this.leaseholdDetailsRevealTargets.find(el => card.contains(el)), detailsOpen)
    if (!detailsOpen) {
      const details = card.querySelector('textarea[name$="[remaining_term_details]"]')
      if (details && details.value !== "") {
        details.value = ""
        details.dispatchEvent(new Event("input", { bubbles: true }))
      }
    }
  }

  #checkedValue(card, nameSuffix) {
    return card.querySelector(`input[name$="${nameSuffix}"]:checked`)?.value
  }

  #currentCount() {
    const form = this.element.closest("form")
    const input = form?.querySelector('input[name="quote_request[portfolio_property_count]"]')
    return parseInt(input?.value, 10)
  }
}
